import {Fragment, type FC, type ReactNode} from "react";
import {Dialog, DialogPanel, DialogTitle, Transition, TransitionChild} from "@headlessui/react";
import {Box, Button, Flex, Text} from "@chakra-ui/react";
import SmartIcon from "../SmartIcon.tsx";

// Headless UI Dialog opened from inside the pricing dialog: as a nested dialog it owns focus, outside clicks
// and Escape while it is up, so the pricing dialog underneath stays mounted and cannot close by accident.
// Dismissing here never reaches the API; only the confirm button submits.

// "unknown": the request may have committed but no outcome arrived; it stays confirmable so the retry replays it.
export type BillingActionPhase = "loading" | "ready" | "pending" | "unknown" | "processing" | "failed" | "done";

export interface BillingSummaryRow {
  label: string;
  value: string;
}

export const SIMULATED_NOTE =
  "Payments are simulated on staging: no card is charged and refunds are recorded, not paid out.";
export const POLAR_CHECKOUT_NOTE = "You'll pay in Polar's secure checkout after you confirm.";
export const POLAR_REFUND_NOTE = "Refunds are paid back to the original card through Polar.";

const tabularNums = {fontVariantNumeric: "tabular-nums"} as const;

// The app runs in Chakra dark mode; the white panel needs explicit light colors on every control.
const solidButtonStyle = {
  size: "md",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  bg: "brandPurple.500",
  color: "white",
  _hover: {bg: "violetPurple.500"},
  _active: {bg: "brandPurple.600"},
} as const;

const outlineButtonStyle = {
  size: "md",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  variant: "outline",
  bg: "white",
  borderColor: "n.300",
  color: "n.700",
  _hover: {bg: "n.200"},
  _active: {bg: "n.300"},
} as const;

interface BillingActionConfirmationProps {
  isOpen: boolean;
  title: string;
  intro?: string | null;
  rows: BillingSummaryRow[];
  note?: string | null;
  phase: BillingActionPhase;
  message: string | null;
  confirmLabel: string;
  dismissLabel: string;
  doneLabel?: string;
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  children?: ReactNode;
}

const BillingActionConfirmation: FC<BillingActionConfirmationProps> = ({
  isOpen,
  title,
  intro = null,
  rows,
  note = SIMULATED_NOTE,
  phase,
  message,
  confirmLabel,
  dismissLabel,
  doneLabel = "Done",
  isConfirmDisabled = false,
  onConfirm,
  onDismiss,
  children,
}) => {
  const isPending = phase === "pending";
  const isResult = phase === "processing" || phase === "failed" || phase === "done";
  const dismissUnlessPending = (): void => {
    if (!isPending) onDismiss();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="fixed inset-0 z-[70] flex p-6 overflow-auto md:px-4" onClose={dismissUnlessPending}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-n-700/75" aria-hidden="true"/>
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel
            className="relative z-10 m-auto w-full max-w-[30rem] rounded-3xl bg-white p-6 text-n-700"
            data-testid="billing-action-dialog"
            data-phase={phase}
          >
            <DialogTitle as="h2" className="h6 pr-12 text-n-700">{title}</DialogTitle>
            {intro && <Text className="text-pretty" mt={2} fontSize="sm" color="n.500">{intro}</Text>}
            {children}
            {phase === "loading" ? (
              <Text mt={4} fontSize="sm" color="n.400" data-testid="billing-summary-loading">
                Preparing the details...
              </Text>
            ) : rows.length > 0 && (
              <Box as="dl" mt={4} px={4} py={2} rounded="2xl" bg="n.200" data-testid="billing-summary">
                {rows.map((row, index) => (
                  <Flex
                    key={row.label}
                    justify="space-between"
                    align="baseline"
                    gap={4}
                    py={2}
                    borderTopWidth={index === 0 ? 0 : "1px"}
                    borderColor="n.300"
                  >
                    <Text as="dt" flexShrink={0} fontSize="sm" color="n.500">{row.label}</Text>
                    <Text as="dd" fontSize="sm" fontWeight="semibold" color="n.700" textAlign="right" sx={tabularNums}>
                      {row.value}
                    </Text>
                  </Flex>
                ))}
              </Box>
            )}
            {note && !isResult && <Text mt={3} fontSize="xs" color="n.400">{note}</Text>}
            {message && (
              <Text mt={3} fontSize="sm" color="sunsetAmber.800" role="alert" data-testid="billing-action-message">
                {message}
              </Text>
            )}
            <Flex justify="flex-end" gap={3} mt={6}>
              {isResult ? (
                <Button {...solidButtonStyle} onClick={onDismiss}>{doneLabel}</Button>
              ) : (
                <>
                  <Button {...outlineButtonStyle} isDisabled={isPending} onClick={onDismiss}>{dismissLabel}</Button>
                  <Button
                    {...solidButtonStyle}
                    isLoading={isPending}
                    isDisabled={phase === "loading" || isConfirmDisabled}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </Button>
                </>
              )}
            </Flex>
            <button
              type="button"
              aria-label="Close dialog"
              className="absolute top-5 right-5 flex h-9 w-9 items-center justify-center rounded-full bg-n-200 fill-n-700 text-0 transition-colors hover:bg-n-300 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={dismissUnlessPending}
              disabled={isPending}
            >
              <SmartIcon className="fill-inherit transition-colors" name="close" size={20}/>
            </button>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default BillingActionConfirmation;
